/**
 * Common styled components for reuse across the application
 * This helps maintain consistent styling and reduces duplication
 */

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme, media, onDark } from './theme';
import { brand } from './brand';

// ======== Layout Components ========

export const PageContainer = styled.div`
  max-width: 1400px;
  margin: ${theme.spacing.lg} auto;
  padding: 0 2rem;

  ${media.tablet} {
    padding: 0 1rem;
  }

  ${media.mobile} {
    padding: 0 0.75rem;
  }
`;

export const Section = styled.section`
  margin-bottom: ${theme.spacing.xl};
`;

export const FlexContainer = styled.div<{
  direction?: 'row' | 'column';
  justify?: string;
  align?: string;
  gap?: keyof typeof theme.spacing;
}>`
  display: flex;
  flex-direction: ${props => props.direction || 'row'};
  justify-content: ${props => props.justify || 'flex-start'};
  align-items: ${props => props.align || 'stretch'};
  gap: ${props => props.gap ? theme.spacing[props.gap] : 0};
`;

export const Grid = styled.div<{
  columns?: string;
  gap?: keyof typeof theme.spacing;
}>`
  display: grid;
  grid-template-columns: ${props => props.columns || 'repeat(auto-fill, minmax(250px, 1fr))'};
  gap: ${props => props.gap ? theme.spacing[props.gap] : theme.spacing.md};
  
  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

// Flat like the webshop: definition comes from the border, not a shadow.
export const Card = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  border: ${brand.border.card} solid ${theme.colors.border};
  transition: border-color ${theme.transitions.normal};

  &:hover {
    border-color: ${theme.colors.text};
  }
`;

// ======== Typography Components ========

// Headings use the webshop's second family, its 500 weight, and its positive letter-spacing.
// Sizes apply Dawn's heading scale to this app's ramp, the same scale x base it uses.
export const PageTitle = styled.h1`
  font-family: ${theme.fontFamilyHeading};
  font-size: calc(${brand.typography.headingScale} * ${theme.fontSizes.xxxl});
  font-weight: ${theme.fontWeights.medium};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text};
  letter-spacing: ${brand.typography.headingLetterSpacing};
`;

export const SectionTitle = styled.h2`
  font-family: ${theme.fontFamilyHeading};
  font-size: calc(${brand.typography.headingScale} * ${theme.fontSizes.xxl});
  font-weight: ${theme.fontWeights.medium};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text};
  letter-spacing: ${brand.typography.headingLetterSpacing};
`;

export const SubTitle = styled.h3`
  font-family: ${theme.fontFamilyHeading};
  font-size: calc(${brand.typography.headingScale} * ${theme.fontSizes.lg});
  font-weight: ${theme.fontWeights.medium};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text};
  letter-spacing: ${brand.typography.headingLetterSpacing};
`;

export const Text = styled.p`
  margin-bottom: ${theme.spacing.sm};
  line-height: 1.6;
  color: ${theme.colors.lightText};

  ul {
    padding-left: 1.5rem;
    margin-top: ${theme.spacing.xs};
  }
`;

// ======== Form Components ========

export const FormContainer = styled.div`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.card};

  h2, h3 {
    color: ${theme.colors.white};
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

export const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

export const Label = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.xs};
  font-weight: ${theme.fontWeights.medium};
  color: inherit;
`;

export const Input = styled.input`
  width: 100%;
  height: ${brand.metrics.inputHeight};
  padding: 0.5rem 0.75rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.md};
  background-color: ${theme.colors.white};
  transition: border-color ${theme.transitions.fast}, box-shadow ${theme.transitions.fast};

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem ${theme.colors.text};
  }

  &::placeholder {
    color: ${theme.colors.muted};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.md};
  min-height: 120px;
  resize: vertical;
  background-color: ${theme.colors.white};
  transition: border-color ${theme.transitions.fast}, box-shadow ${theme.transitions.fast};

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem ${theme.colors.text};
  }

  &::placeholder {
    color: ${theme.colors.muted};
  }
`;

export const Select = styled.select`
  width: 100%;
  height: ${brand.metrics.inputHeight};
  padding: 0.5rem 0.75rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.md};
  background-color: ${theme.colors.white};
  transition: border-color ${theme.transitions.fast}, box-shadow ${theme.transitions.fast};

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem ${theme.colors.text};
  }
`;

export const ErrorText = styled.span`
  color: ${theme.colors.danger};
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing.xs};
  display: block;
`;

// ======== Button Components ========

/**
 * The webshop's button palette. `primary` is near-black on the page background; `secondary` is
 * the inverse with a hairline border. The remaining variants keep this app's semantic colors,
 * which the webshop has no equivalent for.
 */
const buttonFill = (variant?: string) => {
  switch (variant) {
    case 'outline':
    case 'ghost':
    case 'link':
      return null;
    case 'secondary':
      return { bg: brand.scheme.base.secondaryButton, fg: brand.scheme.base.secondaryButtonText };
    case 'success':
      return { bg: theme.colors.success, fg: theme.colors.white };
    case 'danger':
      return { bg: theme.colors.danger, fg: theme.colors.white };
    case 'accent':
      return { bg: theme.colors.accent, fg: theme.colors.white };
    default:
      return { bg: brand.scheme.base.button, fg: brand.scheme.base.buttonText };
  }
};

/**
 * Dawn's hover: a thin ring offset from the button by a gap in the button's text color, rather
 * than a brightness shift or a lift.
 */
const buttonHoverRing = (variant?: string) => {
  const fill = buttonFill(variant);
  if (!fill) return '';
  return `box-shadow: 0 0 0 1.3px ${fill.fg}, 0 0 0 2.3px ${fill.bg};`;
};

export const Button = styled.button<{
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'outline' | 'accent' | 'ghost' | 'link';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: ${props => {
    switch(props.size) {
      case 'small': return '0.5rem 0.75rem';
      case 'large': return `0.75rem ${brand.metrics.buttonPaddingX}`;
      default: return '0.5rem 1rem';
    }
  }};
  font-size: ${props => {
    switch(props.size) {
      case 'small': return theme.fontSizes.sm;
      case 'large': return theme.fontSizes.md;
      default: return theme.fontSizes.sm;
    }
  }};
  background-color: ${props => buttonFill(props.variant)?.bg ?? 'transparent'};
  color: ${props => {
    switch(props.variant) {
      case 'outline': return theme.colors.primary;
      case 'ghost': return theme.colors.text;
      case 'link': return theme.colors.primary;
      default: return buttonFill(props.variant)?.fg;
    }
  }};
  border: ${props => {
    switch(props.variant) {
      case 'outline': return `${brand.border.button} solid ${theme.colors.text}`;
      case 'secondary': return `${brand.border.button} solid ${theme.colors.text}`;
      case 'ghost': return 'none';
      case 'link': return 'none';
      default: return 'none';
    }
  }};
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  letter-spacing: ${brand.typography.buttonLetterSpacing};
  cursor: pointer;
  transition: box-shadow ${theme.transitions.fast}, background-color ${theme.transitions.fast},
    border-color ${theme.transitions.fast};
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  text-align: center;
  text-decoration: none;

  &:hover {
    ${props => {
      switch(props.variant) {
        case 'outline': return `background-color: ${theme.colors.background};`;
        case 'ghost': return `background-color: ${theme.colors.background};`;
        case 'link': return 'text-decoration: underline;';
        default: return buttonHoverRing(props.variant);
      }
    }}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const LinkButton = styled(Link)<{
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'outline' | 'accent' | 'ghost';
  size?: 'small' | 'medium' | 'large';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: ${props => {
    switch(props.size) {
      case 'small': return '0.5rem 0.75rem';
      case 'large': return `0.75rem ${brand.metrics.buttonPaddingX}`;
      default: return '0.5rem 1rem';
    }
  }};
  font-size: ${props => {
    switch(props.size) {
      case 'small': return theme.fontSizes.sm;
      case 'large': return theme.fontSizes.md;
      default: return theme.fontSizes.sm;
    }
  }};
  background-color: ${props => buttonFill(props.variant)?.bg ?? 'transparent'};
  color: ${props => {
    switch(props.variant) {
      case 'outline': return theme.colors.primary;
      // `ghost` is only used on the dark panels.
      case 'ghost': return theme.colors.white;
      default: return buttonFill(props.variant)?.fg;
    }
  }};
  border: ${props => {
    switch(props.variant) {
      case 'outline': return `${brand.border.button} solid ${theme.colors.text}`;
      case 'secondary': return `${brand.border.button} solid ${theme.colors.text}`;
      case 'ghost': return `${brand.border.button} solid ${onDark.border}`;
      default: return 'none';
    }
  }};
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  letter-spacing: ${brand.typography.buttonLetterSpacing};
  text-decoration: none;
  transition: box-shadow ${theme.transitions.fast}, background-color ${theme.transitions.fast},
    border-color ${theme.transitions.fast};
  text-align: center;

  &:hover {
    text-decoration: none;
    ${props => {
      switch(props.variant) {
        case 'outline': return `background-color: ${theme.colors.background};`;
        case 'ghost': return `background-color: ${onDark.surfaceHover};`;
        default: return buttonHoverRing(props.variant);
      }
    }}
  }
`;

export const TextLink = styled(Link)`
  color: ${theme.colors.primary};
  text-decoration: none;
  font-weight: ${theme.fontWeights.medium};
  
  &:hover {
    text-decoration: underline;
  }
`;

// ======== Table Components ========

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: ${theme.spacing.lg};
`;

export const TableHead = styled.thead`
  background-color: ${theme.colors.gray.lightest};
  border-bottom: ${brand.border.card} solid ${theme.colors.text};
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.gray.light};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${theme.colors.gray.lightest};
  }
`;

export const TableHeader = styled.th`
  padding: ${theme.spacing.sm};
  text-align: left;
  font-weight: ${theme.fontWeights.semibold};
`;

export const TableCell = styled.td`
  padding: ${theme.spacing.sm};
`;

// ======== Alert Component ========

export const Alert = styled.div<{
  variant?: 'success' | 'warning' | 'danger' | 'info'
}>`
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  background-color: ${props => {
    switch(props.variant) {
      case 'success': return brand.scheme.infoGreen;
      case 'warning': return brand.scheme.infoOrange;
      // The webshop has no soft red; this is the danger color composited at 12% over the page.
      case 'danger': return '#fae7e5';
      default: return brand.scheme.infoBlue;
    }
  }};
  border-left: 4px solid ${props => {
    switch(props.variant) {
      case 'success': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'danger': return theme.colors.danger;
      default: return theme.colors.primary;
    }
  }};
  color: ${theme.colors.text};
`;

// ======== Badge Component ========

export const Badge = styled.span<{
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'accent'
}>`
  display: inline-block;
  padding: 0.125rem 0.625rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  border: 1px solid transparent;
  background-color: ${props => {
    switch(props.variant) {
      case 'secondary': return theme.colors.secondary;
      case 'success': return theme.colors.success;
      case 'danger': return theme.colors.danger;
      case 'warning': return theme.colors.warning;
      case 'accent': return theme.colors.accent;
      default: return theme.colors.primary;
    }
  }};
  color: ${theme.colors.white};
`;

// ======== Divider Component ========

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.gray.light};
  margin: ${theme.spacing.md} 0;
`;

// ======== Image Components ========

export const ResponsiveImage = styled.img`
  max-width: 100%;
  height: auto;
`;

// ======== Modal Components ========

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background-color: ${theme.colors.white};
  border: ${brand.border.popup} solid ${theme.colors.text};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

// ======== Foil Badge Component ========

export const FoilBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.secondary});
  color: ${theme.colors.white};
  padding: 0.15rem 0.5rem;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const ModalClose = styled.button`
  position: absolute;
  right: ${theme.spacing.sm};
  top: ${theme.spacing.sm};
  background: transparent;
  border: none;
  font-size: ${theme.fontSizes.xl};
  cursor: pointer;
  color: ${theme.colors.gray.dark};
  
  &:hover {
    color: ${theme.colors.danger};
  }
`;
